/**
 * The control API, from the browser.
 *
 * Two credentials reach this API and they are not interchangeable. A bearer key is for
 * programs (§25, and the MCP server); a session cookie is for the person signed in here.
 * This client only ever uses the second, which is why every request carries
 * `credentials: 'include'` and none of them carry a token. There is no token in this
 * bundle to steal, and nothing in `localStorage` worth reading.
 */

export const API_URL: string = import.meta.env.VITE_API_URL ?? 'http://localhost:8787';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | undefined;

  constructor(status: number, code: string, message: string, requestId?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; request_id?: string };
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body ? { 'content-type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as ErrorBody;
    throw new ApiError(
      res.status,
      body.error?.code ?? 'unknown',
      body.error?.message ?? res.statusText,
      body.error?.request_id,
    );
  }
  return res.json() as Promise<T>;
}

export const get = <T>(path: string) => api<T>(path);
export const put = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'PUT', ...(body === undefined ? {} : { body: JSON.stringify(body) }) });
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });
export const post = <T>(path: string, body?: unknown) =>
  api<T>(path, { method: 'POST', ...(body === undefined ? {} : { body: JSON.stringify(body) }) });

// ── The shapes this app reads ───────────────────────────────────────────────
// Hand-written rather than generated: the API is stable and versioned, and a generator
// would pull in every admin route the app has no business knowing about.

export interface EvidenceRef {
  observation_id: string;
  observed_at: string;
  source_url: string | null;
  archive_url: string | null;
  evidence_class: 'observed' | 'archived' | string;
  snapshot_url: string;
  redacted: { at: string; reason: string | null } | null;
}

export interface Move {
  id: string;
  type: string;
  company: { slug: string; name: string };
  occurred_between: string;
  detected_at: string;
  uncertainty_days: number;
  /** §12.4: beyond 45 days of uncertainty a move is a PERIOD and must not be shown as a date. */
  dating: 'point' | 'period';
  evidence_class: 'observed' | 'archived' | string;
  subject: {
    plan?: { rung: number; canonical_tier: string; name: string | null };
    feature?: { slug: string; name: string };
  };
  delta: Record<string, unknown>;
  evidence: {
    before: EvidenceRef | null;
    after: EvidenceRef | null;
    excerpt_url: string;
  };
}

export interface DigestItem {
  headline: string;
  score: number;
  components: Record<string, number>;
  move: Move;
}

export interface Digest {
  week_of: string;
  quiet: boolean;
  quiet_line: string | null;
  items: DigestItem[];
  market_context: string | null;
  /** Candidates dropped before ranking, each with the rule that dropped it. */
  excluded: { move_id: string; reason: string }[];
}

export interface Me {
  account: { id: string; email: string | null; plan: string; created_at: string | null };
  watching: number;
  watchlist_limit: number | null;
  limits: { private_surfaces: number; requests_per_minute: number; seats: number | null };
  self_profile: { name: string; domain: string; completeness: string } | null;
  countermove_wave: number;
}

export interface CommercialPlan {
  id: 'free' | 'pro' | 'scale';
  name: string;
  annual_price: number;
  watchlist_limit: number | null;
  private_surface_limit: number;
  requests_per_minute: number;
  seats: number | null;
  features: string[];
}

export interface BillingStatus {
  customer: boolean;
  subscription: {
    status: string;
    plan: 'free' | 'pro' | 'scale';
    current_period_end: string | null;
    cancel_at_period_end: boolean;
  } | null;
}

export interface Company {
  slug: string;
  name: string;
  domain: string;
  active: boolean;
  markets: { slug: string; name: string }[];
  plans: PlanSet;
}

export interface PricePoint {
  /** null with basis 'quote' is "contact us". Never 0, never a guess. */
  amount: number | null;
  currency: string;
  period: 'month' | 'year' | 'once';
  basis: 'per_seat' | 'flat' | 'usage' | 'quote';
}

export interface Plan {
  rung: number;
  name: string;
  prices: PricePoint[];
  seat_limit: number | null;
  trial_days: number | null;
  features: { slug: string; name: string }[];
  /** §15: shown, never counted, and never bucketed as "other". */
  unresolved_terms: string[];
}

export interface PlanSet {
  as_of: string | null;
  /** §14 / I3: whether the PAGE was read correctly. Never a claim about the world. */
  parse_quality: 'clean' | 'partial' | 'failed' | null;
  observation_id?: string;
  plans: Plan[];
}

export interface Excerpt {
  lines?: string;
  max_lines?: number;
  source_url?: string | null;
  archive_url?: string | null;
  observed_at?: string;
  attribution?: string | null;
  note?: string;
  /** §37.3: the body was removed; the claim it grounded stays auditable. */
  tombstone?: boolean;
  reason?: string | null;
  redacted_at?: string;
  message?: string;
}

export interface Hypothesis {
  id: string;
  company_slug: string | null;
  pattern: string;
  label: string;
  status: 'emerging' | 'corroborated' | 'contradicted' | 'dormant' | string;
  /** §19.5 / I3: counts and spans. There is no percentage in this object by design. */
  confidence: {
    supporting_count: number;
    countervailing_count: number;
    distinct_surfaces: number;
    span_days: number;
    pattern_share: number | null;
    market_baseline: number | null;
    summary: string;
  };
  gate_relaxed: boolean;
  first_signal_at: string;
  last_signal_at: string;
  evidence: { move_id: string; role: string }[];
  /** §19.4: what would prove this wrong, by table lookup rather than by model. */
  falsified_by: { move_type: string; description: string }[];
  status_log: { from: string | null; to: string; occurred_at: string }[];
}

export interface Coverage {
  company: { slug: string; name: string };
  surfaces: {
    kind: string;
    url: string;
    last_status: number | null;
    last_fetch_at: number | null;
    backfill_status: string | null;
    consecutive_failures: number;
    pin_reason: string | null;
  }[];
  [key: string]: unknown;
}

export interface WatchlistEntry {
  slug: string;
  name: string;
  domain: string;
  added_at?: string;
}

// ── §20 the self-model, §22 battlecards ─────────────────────────────────────

export interface SelfProfile {
  id: string;
  name: string;
  domain: string;
  market_id: string | null;
  icp: string | null;
  positioning: string | null;
  completeness: 'minimal' | 'partial' | 'complete';
  created_at: string;
}

/** A proposal and a confirmed differentiator are the same row at different `source`s. */
export interface Differentiator {
  id: string;
  kind: 'differentiator' | 'known_gap' | string;
  source: 'proposed' | 'confirmed' | 'user_added' | string;
  /** Feature-agnostic by design: the feature is named separately. */
  claim: string;
  feature: { slug: string; name: string } | null;
}

/** §20: paid rungs only; a free tier is not a tier. */
export interface LadderRange {
  min: number | null;
  max: number | null;
}

export interface SelfResponse {
  self: SelfProfile;
  market: { slug: string; name: string } | null;
  differentiators: Differentiator[];
  tier_range: LadderRange;
  pricing: PricingRead | null;
}

/** How far the read of the customer's OWN pricing page has got. */
export interface PricingRead {
  url: string;
  /** In flight, or not yet fetched once. */
  reading: boolean;
  /** The crawler's own word: ok, unchanged, not_found, blocked, auth_required, error. */
  last_status: string | null;
  last_error: string | null;
  last_fetch_at: string | null;
  /** What the extractor made of the page: clean, partial, failed, or null if unread. */
  parse_quality: string | null;
  plans_found: number;
  /** §20's gate: a ladder came out of it. A page can be fetched and still say nothing. */
  read_cleanly: boolean;
}

export interface SelfCreated {
  self: SelfProfile;
  proposals: Differentiator[];
  tier_range: LadderRange;
}

export interface Completeness {
  completeness: 'minimal' | 'partial' | 'complete';
  /** §21: which response classes may be SHOWN. Never what is computed. */
  classes_unlocked: string[];
  battlecards: boolean;
  missing: string[];
  unlocks: string[];
}

export interface LadderEntry {
  rung: number;
  name: string;
  monthly: number | null;
}

export interface FeatureCell {
  feature_id: string;
  feature_name: string;
  /** Lowest rung offering it; null where the company does not offer it at all. */
  self_rung: number | null;
  competitor_rung: number | null;
}

export interface Battlecard {
  competitor: { id: string; name: string };
  price_ladder: {
    self: LadderEntry[];
    competitor: LadderEntry[];
    per_tier_delta: { rung: number; delta: number | null }[];
  };
  feature_matrix: FeatureCell[];
  where_we_win: { feature: { slug: string; name: string } | null; claim: string }[];
  where_they_win: { feature: { slug: string; name: string } | null; claim: string }[];
  recent_moves: Move[];
  active_hypotheses: {
    id: string; pattern: string; label: string; status: string;
    supporting_count: number; countervailing_count: number;
  }[];
  /** §22: competitor features at a LOWER rung than ours. What loses the deal. */
  landmines: { feature_id: string; feature_name: string; self_rung: number; competitor_rung: number }[];
  /** §22: the one generated section. Null until a provider produces one that validates. */
  talk_track: string | null;
  evidence_hash: string;
  evidence_as_of: string | null;
  stale: boolean;
  source: 'cached' | 'rebuilt';
}

export interface BattlecardIndex {
  available: boolean;
  reason: string | null;
  cards: { competitor: { id: string; slug: string; name: string }; evidence_as_of: string | null; stale: boolean }[];
}
